import { useEffect, useMemo, useState } from 'react';
import {
  X, MagnifyingGlass, ArrowLeft, CaretRight, BookOpen,
  House, Palette, Article, Images, FilmStrip, FilePdf,
  PaperPlaneTilt, RssSimple, ImageSquare, ChartBar, UserCircle,
  CheckCircle, Warning, Lightbulb, Info
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { hasTourForView } from './ProductTour';

const ICONS = {
  House, Palette, Article, Images, FilmStrip, FilePdf,
  PaperPlaneTilt, RssSimple, ImageSquare, ChartBar, UserCircle,
};

const FALLBACK_CATALOG = {
  version: 2,
  categories: [
    { id: 'start', icon: 'House', color: '#6366f1', title: 'Primi passi', desc: 'Orientamento e flusso di lavoro' },
  ],
  articles: [
    {
      id: 'help-unavailable',
      categoryId: 'start',
      title: 'Guida temporaneamente non disponibile',
      desc: 'Riprova tra poco.',
      content: [{ type: 'para', text: 'Non è stato possibile caricare la knowledge base ufficiale. Chiudi e riapri la Guida tra qualche secondo.' }],
    },
  ],
};

function Block({ block }) {
  const style = { color: 'var(--text-secondary)', lineHeight: 1.7 };
  if (block.type === 'h3') return <h3 className="mt-6 mb-2 text-base font-bold text-[var(--text-primary)]">{block.text}</h3>;
  if (block.type === 'para') return <p className="mb-3 text-sm" style={style}>{block.text}</p>;
  if (block.type === 'bullets') return (
    <ul className="mb-4 ml-5 list-disc space-y-1.5 text-sm" style={style}>
      {(block.items || []).map((item, index) => <li key={index}>{item}</li>)}
    </ul>
  );
  if (block.type === 'steps') return (
    <div className="mb-5 space-y-2.5">
      {(block.items || []).map((item, index) => (
        <div key={index} className="flex gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] p-3">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ background: 'linear-gradient(135deg, var(--gradient-start), var(--gradient-end))' }}>
            {index + 1}
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--text-primary)]">{item.title}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-[var(--text-muted)]">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
  if (block.type === 'grid') return (
    <div className="mb-5 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
      {(block.items || []).map((item, index) => (
        <div key={index} className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] p-3">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{item.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-[var(--text-muted)]">{item.desc}</p>
        </div>
      ))}
    </div>
  );
  if (['tip', 'note', 'warn'].includes(block.type)) {
    const meta = block.type === 'tip'
      ? { Icon: Lightbulb, border: 'rgba(99,102,241,.35)', bg: 'rgba(99,102,241,.10)' }
      : block.type === 'warn'
        ? { Icon: Warning, border: 'rgba(245,158,11,.35)', bg: 'rgba(245,158,11,.10)' }
        : { Icon: Info, border: 'rgba(6,182,212,.35)', bg: 'rgba(6,182,212,.08)' };
    const Icon = meta.Icon;
    return (
      <div className="mb-4 flex gap-2.5 rounded-xl border p-3 text-sm leading-relaxed text-[var(--text-secondary)]"
        style={{ borderColor: meta.border, background: meta.bg }}>
        <Icon size={17} className="mt-0.5 flex-shrink-0" />
        <span>{block.text}</span>
      </div>
    );
  }
  return null;
}

function CapabilityBox({ title, items, positive }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] p-3">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">{title}</p>
      <div className="space-y-1.5">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-2 text-xs leading-relaxed text-[var(--text-secondary)]">
            {positive ? <CheckCircle size={14} weight="fill" className="mt-0.5 flex-shrink-0 text-emerald-400" /> : <Warning size={14} className="mt-0.5 flex-shrink-0 text-amber-300" />}
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArticleCard({ article, category, onClick }) {
  const Icon = ICONS[category?.icon] || Article;
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-2 flex w-full items-center gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] p-4 text-left transition-colors hover:border-white/20"
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: `${category?.color || '#6366f1'}20` }}>
        <Icon size={18} color={category?.color || '#6366f1'} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[var(--text-primary)]">{article.title}</p>
        <p className="mt-0.5 text-xs text-[var(--text-muted)]">{article.desc}</p>
      </div>
      <CaretRight size={15} className="flex-shrink-0 text-[var(--text-muted)]" />
    </button>
  );
}

export default function HelpCenter({ onClose, initialArticleId = null, activeView = 'dashboard', onStartTour }) {
  const { api } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [catId, setCatId] = useState(null);
  const [articleId, setArticleId] = useState(initialArticleId);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/help/catalog')
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data?.categories) && Array.isArray(data?.articles)) {
          setCatalog(data);
          setLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalog(FALLBACK_CATALOG);
          setLoadError(true);
        }
      });
    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => {
    if (initialArticleId) setArticleId(initialArticleId);
  }, [initialArticleId]);

  useEffect(() => {
    if (!catalog || !articleId) return;
    const article = catalog.articles.find(item => item.id === articleId);
    if (article) setCatId(article.categoryId);
  }, [catalog, articleId]);

  const categories = useMemo(() => catalog?.categories || [], [catalog]);
  const articles = useMemo(() => catalog?.articles || [], [catalog]);
  const categoriesById = useMemo(() => Object.fromEntries(categories.map(category => [category.id, category])), [categories]);

  const currentCategory = categoriesById[catId] || null;
  const currentArticle = articles.find(article => article.id === articleId) || null;
  const categoryArticles = articles.filter(article => article.categoryId === catId);

  const searchResults = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    const flatten = article => JSON.stringify({
      title: article.title,
      desc: article.desc,
      content: article.content,
      can_do: article.can_do,
      cannot_do: article.cannot_do,
    }).toLowerCase();
    return articles.filter(article => flatten(article).includes(query));
  }, [articles, search]);

  const goHome = () => {
    setCatId(null);
    setArticleId(null);
    setSearch('');
  };

  if (!catalog) {
    return (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-center">
          <div className="mx-auto mb-3 h-10 w-10 animate-pulse rounded-full" style={{ background: 'linear-gradient(135deg,var(--gradient-start),var(--gradient-end))' }} />
          <p className="text-sm text-[var(--text-muted)]">Caricamento guida…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col bg-[var(--bg-primary)]" data-testid="help-center-v2">
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 md:px-6">
        <button type="button" onClick={goHome} className="flex items-center gap-2 font-bold text-[var(--text-primary)]">
          <img src="/assets/favicon.jpg" alt="" className="h-7 w-7 rounded-md object-contain" />
          <span className="hidden sm:inline">Sketchario Help</span>
        </button>
        <div className="relative min-w-0 max-w-xl flex-1">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={search}
            onChange={event => { setSearch(event.target.value); setArticleId(null); setCatId(null); }}
            placeholder="Cerca nella guida…"
            className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] py-2 pl-9 pr-3 text-sm outline-none"
          />
        </div>
        <span className="hidden md:inline text-[10px] text-[var(--text-muted)]">KB v{catalog.version || 2}</span>
        {onStartTour && hasTourForView(activeView) && (
          <button type="button" className="btn-ghost hidden sm:inline-flex text-xs" onClick={onStartTour}>
            Rivedi tour pagina
          </button>
        )}
        <button type="button" className="btn-ghost p-2" onClick={onClose} aria-label="Chiudi guida"><X size={17} /></button>
      </header>

      {loadError && (
        <div className="border-b border-amber-400/30 bg-amber-400/10 px-4 py-2 text-xs text-amber-100">
          La knowledge base online non è raggiungibile: sto mostrando il fallback locale.
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-64 flex-shrink-0 overflow-y-auto border-r border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 md:block">
          <button type="button" onClick={goHome} className="mb-3 flex items-center gap-2 px-2 py-2 text-xs text-[var(--text-muted)] hover:text-white">
            <BookOpen size={14} /> Indice
          </button>
          {categories.map(category => {
            const Icon = ICONS[category.icon] || Article;
            const active = catId === category.id;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => { setCatId(category.id); setArticleId(null); setSearch(''); }}
                className="mb-1 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition-colors"
                style={{ background: active ? `${category.color}18` : 'transparent', color: active ? category.color : 'var(--text-secondary)' }}
              >
                <Icon size={16} weight={active ? 'fill' : 'regular'} />
                <span>{category.title}</span>
              </button>
            );
          })}
        </aside>

        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-7">
          {search.trim() ? (
            <div className="mx-auto max-w-4xl">
              <p className="mb-4 text-xs text-[var(--text-muted)]">{searchResults.length} risultati per “{search.trim()}”</p>
              {searchResults.map(article => (
                <ArticleCard key={article.id} article={article} category={categoriesById[article.categoryId]} onClick={() => { setArticleId(article.id); setCatId(article.categoryId); setSearch(''); }} />
              ))}
              {searchResults.length === 0 && <p className="text-sm text-[var(--text-muted)]">Nessun risultato.</p>}
            </div>
          ) : currentArticle ? (
            <article className="mx-auto max-w-3xl">
              <button type="button" className="mb-5 flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-white" onClick={() => setArticleId(null)}>
                <ArrowLeft size={13} /> {currentCategory?.title || 'Indice'}
              </button>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: currentCategory?.color || 'var(--accent-purple)' }}>{currentCategory?.title}</p>
              <h1 className="text-2xl font-black text-[var(--text-primary)]">{currentArticle.title}</h1>
              <p className="mb-6 mt-2 text-sm text-[var(--text-muted)]">{currentArticle.desc}</p>
              <div className="border-t border-[var(--border-color)] pt-5">
                {(currentArticle.content || []).map((block, index) => <Block key={index} block={block} />)}
              </div>
              {(currentArticle.can_do?.length || currentArticle.cannot_do?.length) ? (
                <div className="mt-7 grid gap-3 md:grid-cols-2">
                  <CapabilityBox title="Puoi farlo" items={currentArticle.can_do} positive />
                  <CapabilityBox title="Limiti / da sapere" items={currentArticle.cannot_do} />
                </div>
              ) : null}
            </article>
          ) : currentCategory ? (
            <div className="mx-auto max-w-4xl">
              <div className="mb-5">
                <h1 className="text-xl font-black text-[var(--text-primary)]">{currentCategory.title}</h1>
                <p className="mt-1 text-sm text-[var(--text-muted)]">{currentCategory.desc}</p>
              </div>
              {categoryArticles.map(article => <ArticleCard key={article.id} article={article} category={currentCategory} onClick={() => setArticleId(article.id)} />)}
            </div>
          ) : (
            <div className="mx-auto max-w-5xl">
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--accent-purple)]">Knowledge base ufficiale</p>
                <h1 className="mt-1 text-2xl font-black text-[var(--text-primary)]">Come possiamo aiutarti?</h1>
                <p className="mt-2 max-w-2xl text-sm text-[var(--text-muted)]">Questa è la stessa base informativa usata dagli help contestuali e dall'assistente AI di Sketchario.</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {categories.map(category => {
                  const Icon = ICONS[category.icon] || Article;
                  const count = articles.filter(article => article.categoryId === category.id).length;
                  return (
                    <button key={category.id} type="button" onClick={() => setCatId(category.id)}
                      className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] p-4 text-left transition-transform hover:-translate-y-0.5">
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${category.color}20` }}>
                        <Icon size={20} color={category.color} weight="duotone" />
                      </div>
                      <p className="text-sm font-bold text-[var(--text-primary)]">{category.title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-[var(--text-muted)]">{category.desc}</p>
                      <p className="mt-3 text-[10px] font-semibold" style={{ color: category.color }}>{count} {count === 1 ? 'articolo' : 'articoli'}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
